import { remove } from "@src/appV2/api";
import { useCbhQueryClient } from "@src/appV2/api/queryClient";
import { environmentConfig } from "@src/appV2/environment";
import { APP_V2_APP_EVENTS } from "@src/appV2/lib";
import { logEvent } from "@src/appV2/lib/analytics";
import {
  cancelPlacementFetchingQueries,
  invalidatePlacementFetchingQueries,
  optimisticallySetPlacementFavoritedState,
} from "@src/appV2/redesign/Placements/api/placementFavoriteCacheUtils";
import { useMutation, type UseMutationOptions } from "@tanstack/react-query";
import { AxiosError, HttpStatusCode } from "axios";
import { z } from "zod";

// FIXME: Automatically disabled during ESLint config update
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const deleteWorkerFavoritePlacementParamsSchema = z.object({
  workerId: z.string(),
  workerFavoritePlacementId: z.string().uuid(),
  placementId: z.string(),
  source: z.string(),
});

type DeleteWorkerFavoritePlacementParams = z.infer<
  typeof deleteWorkerFavoritePlacementParamsSchema
>;

export function useDeleteWorkerFavoritePlacement(
  options: UseMutationOptions<undefined, string, DeleteWorkerFavoritePlacementParams> = {}
) {
  const queryClient = useCbhQueryClient();

  return useMutation({
    mutationFn: async (params: DeleteWorkerFavoritePlacementParams) => {
      const { workerId, workerFavoritePlacementId, placementId } = params;

      await cancelPlacementFetchingQueries(queryClient, workerId, placementId);

      optimisticallySetPlacementFavoritedState(queryClient, workerId, placementId, false);

      try {
        await remove({
          url: `${environmentConfig.REACT_APP_BASE_API_URL}/workers/${workerId}/favorite-placements/${workerFavoritePlacementId}`,
          responseSchema: z.any(),
        });
      } catch (error) {
        // ignore a 404 since that means the favorite placement is already deleted
        if (error instanceof AxiosError && error.status === HttpStatusCode.NotFound) {
          return undefined;
        }

        throw error;
      }

      return undefined;
    },
    onSuccess: async (_data, variables, _context) => {
      const { workerId, placementId, source } = variables ?? {};

      logEvent(APP_V2_APP_EVENTS.PLACEMENT_UNFAVORITED, {
        workerId,
        placementId,
        source,
      });

      await invalidatePlacementFetchingQueries(queryClient, workerId, placementId);
    },
    meta: {
      logErrorMessage: APP_V2_APP_EVENTS.PLACEMENT_UNFAVORITED_FAILURE,
    },
    ...options,
  });
}
